import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getMyVenueStatus } from "@/lib/applications.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/venue-pending")({
  head: () => ({ meta: [{ title: "Pending Approval — The Hospitality Reserve" }] }),
  component: VenuePendingPage,
});

function VenuePendingPage() {
  const navigate = useNavigate();
  const fetchStatus = useServerFn(getMyVenueStatus);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await fetchStatus();
      if (r.role !== "venue") { navigate({ to: "/dashboard", replace: true }); return; }
      if (r.status === "approved") { navigate({ to: "/venue", replace: true }); return; }
      if (!r.venue_onboarding_completed_at) { navigate({ to: "/venue/onboarding", replace: true }); return; }
      setStatus(r.status);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  if (loading) {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  const rejected = status === "rejected";

  return (
    <div className="min-h-dvh bg-background">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-2xl px-6 py-20 text-center">
        <p className="tracking-wide-luxury text-accent">
          {rejected ? "Application Unsuccessful" : "Awaiting Approval"}
        </p>
        <h1 className="mt-3 font-display text-3xl">
          {rejected ? "Your venue account was not approved" : "Your venue is under review"}
        </h1>
        <span className="gold-divider mt-6 mx-auto" />
        <p className="mt-8 text-muted-foreground leading-relaxed">
          {rejected
            ? "Thank you for applying to The Hospitality Reserve. After review, your venue account was not approved. If you believe this is in error, please reach out to our team."
            : "Thank you for registering. Every venue must be approved by an administrator before it can post shifts and hire bartenders. We'll notify you as soon as a decision has been made — this usually takes less than 24 hours."}
        </p>

        {!rejected && (
          <div className="mt-8 border border-border bg-card p-5 text-left">
            <p className="text-xs tracking-wide-luxury text-accent">Need to fix something?</p>
            <p className="mt-2 text-sm text-muted-foreground">
              You can update your business name, contact details, or address at any
              time until an admin makes a decision.
            </p>
            <Link
              to="/venue/onboarding"
              className="mt-4 inline-block border border-accent text-accent px-4 py-2 text-xs tracking-wide-luxury hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Update onboarding details
            </Link>
          </div>
        )}

        <div className="mt-10">
          <Button onClick={handleSignOut} variant="outline"
            className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white tracking-wide-luxury">
            Sign Out
          </Button>
        </div>
      </main>
    </div>
  );
}
